import { useEffect, useState } from "react";

interface Props<T, E> {
	errors: E | undefined;
	onChange: (evt) => void;
	onSubmit: (evt?) => void;
	hasChanges: boolean;
	isSubmitting: boolean;
	resetValues: (e) => void;
	setErrors: React.Dispatch<React.SetStateAction<E | undefined>>;
	setHasChanges: React.Dispatch<React.SetStateAction<boolean>>;
	setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
	setValue: React.Dispatch<React.SetStateAction<Partial<T>>>;
	values: Partial<T | undefined>;
}

const useForm = <T, E>(callback: () => void, initialState: T, validate: (values: T) => E): Props<T, E> => {
	const [hasChanges, setHasChanges] = useState(false);
	const [values, setValues] = useState<T>(initialState);
	const [errors, setErrors] = useState<E | undefined>(undefined);
	const [isSubmitting, setIsSubmitting] = useState(false);

	useEffect(() => {
		if (errors && Object.keys(errors).length === 0 && isSubmitting) {
			callback();
		}
	}, [callback, errors, isSubmitting]);

	const onChange = event => {
		const newValues = {
			...values,
			[event.target.name]:
				!event.target.type || event.target.type === "text" ? event.target.value : event.target.checked
		};
		setValues(newValues);
		if (!hasChanges) {
			setHasChanges(true);
		}
		if (errors && Object.keys(errors).length > 0) {
			const errs = validate(newValues);
			setErrors(errs);
		}
		setIsSubmitting(false);
	};

	const onSubmit = (event?) => {
		if (event) {
			event.preventDefault();
		}
		setIsSubmitting(true);
		const errs = validate(values);
		setErrors(errs);
		if (errs && Object.keys(errs).length > 0) {
			setIsSubmitting(false);
		}
	};

	const resetValues = newValue => {
		if (newValue) {
			setValues(newValue);
			setErrors(undefined);
		} else {
			setValues(initialState);
			setErrors(undefined);
		}
	};

	const setValue = value => {
		setValues({
			...values,
			...value
		});
	};

	return {
		errors,
		onChange,
		onSubmit,
		isSubmitting,
		hasChanges,
		resetValues,
		setErrors,
		setHasChanges,
		setIsSubmitting,
		setValue,
		values
	};
};

export default useForm;
